import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import { ChevronLeftRounded, ChevronRightRounded } from "@mui/icons-material";
import { alpha } from "@mui/material/styles";
import { EmptyState, PageIntro, PageShell, SurfaceCard } from "../ui/AppPrimitives";

const normalize = (date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();

const CalendarView = ({ dates }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDayIndex = new Date(year, month, 1).getDay();
  const startDay = firstDayIndex === 0 ? 6 : firstDayIndex - 1;
  const todayNormalized = normalize(new Date());

  const calendarDays = useMemo(() => {
    const days = [];
    for (let i = 0; i < startDay; i += 1) days.push(null);
    for (let d = 1; d <= daysInMonth; d += 1) days.push(new Date(year, month, d));
    return days;
  }, [daysInMonth, month, startDay, year]);

  const eventsMap = useMemo(() => {
    const map = {};
    dates.forEach((item) => {
      if (!item.dateTime) return;
      const eventDate = new Date(item.dateTime);
      const keyDate =
        item.type === "recurring"
          ? new Date(year, eventDate.getMonth(), eventDate.getDate())
          : eventDate;
      const key = normalize(keyDate);
      if (!map[key]) map[key] = [];
      map[key].push(item);
    });
    return map;
  }, [dates, year]);

  const selectedEvents = eventsMap[normalize(selectedDate)] || [];

  if (dates.length === 0) {
    return (
      <PageShell maxWidth="lg">
        <EmptyState
          title="No dates on the calendar yet"
          description="Once you add events, this calendar will highlight them so you can scan your month at a glance."
          actionLabel="Add a date"
          actionTo="/add"
        />
      </PageShell>
    );
  }

  return (
    <PageShell maxWidth="xl">
      

      <Grid container spacing={3}>
        <Grid item xs={12} lg={8}>
          <SurfaceCard sx={{ p: { xs: 2, md: 3 } }}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              spacing={1}
              sx={{ mb: 2.5 }}
            >
              <Button
                onClick={() => setCurrentDate(new Date(year, month - 1, 1))}
                startIcon={<ChevronLeftRounded />}
                sx={{ minWidth: { xs: 0, sm: 110 }, px: { xs: 1, sm: 2 } }}
              >
                Previous
              </Button>
              <Typography variant="h3" sx={{ textAlign: "center", flex: 1, px: 1 }}>
                {currentDate.toLocaleString("default", { month: "long", year: "numeric" })}
              </Typography>
              <Button
                onClick={() => setCurrentDate(new Date(year, month + 1, 1))}
                endIcon={<ChevronRightRounded />}
                sx={{ minWidth: { xs: 0, sm: 110 }, px: { xs: 1, sm: 2 } }}
              >
                Next
              </Button>
            </Stack>

            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, minmax(0, 1fr))", gap: { xs: 0.6, sm: 1 } }}>
              {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((day) => (
                <Typography key={day} variant="body2" sx={{ textAlign: "center", fontWeight: 700, color: "text.secondary", pb: 0.5 }}>
                  {day}
                </Typography>
              ))}

              {calendarDays.map((date, index) => {
                if (!date) {
                  return <Box key={`empty-${index}`} sx={{ minHeight: 72 }} />;
                }

                const normalized = normalize(date);
                const hasEvents = eventsMap[normalized];
                const hasImportant = hasEvents?.some((event) => event.priority);
                const hasRecurring = hasEvents?.some((event) => event.type === "recurring");
                const isToday = normalized === todayNormalized;
                const isSelected = normalized === normalize(selectedDate);

                return (
                  <Button
                    key={normalized}
                    onClick={() => setSelectedDate(date)}
                    sx={{
                      minHeight: { xs: 50, sm: 68 },
                      borderRadius: { xs: "18px", sm: "22px" },
                      border: `1px solid ${
                        isToday ? "#22c55e" : hasRecurring ? "#c4b5fd" : "rgba(148, 163, 184, 0.22)"
                      }`,
                      bgcolor: isSelected
                        ? "primary.main"
                        : hasImportant
                          ? "rgba(245, 158, 11, 0.16)"
                          : hasRecurring
                            ? "rgba(124, 58, 237, 0.10)"
                            : hasEvents
                              ? "rgba(37, 99, 235, 0.08)"
                              : "rgba(255,255,255,0.78)",
                      color: isSelected ? "white" : "text.primary",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      p: { xs: 0.85, sm: 1.1 },
                      minWidth: 0,
                    }}
                  >
                    <Typography sx={{ fontWeight: 700 }}>{date.getDate()}</Typography>
                    {hasEvents && (
                      <Box
                        sx={{
                          alignSelf: "flex-end",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          minWidth: { xs: 18, sm: 24 },
                          height: { xs: 18, sm: 24 },
                          px: { xs: 0.4, sm: 0.8 },
                          borderRadius: 999,
                          bgcolor: isSelected ? "rgba(255,255,255,0.18)" : alpha("#ffffff", 0.82),
                          color: isSelected ? "white" : "text.secondary",
                          fontSize: { xs: "0.65rem", sm: "0.75rem" },
                          fontWeight: 700,
                          lineHeight: 1,
                          maxWidth: "100%",
                        }}
                      >
                        {hasEvents.length}
                      </Box>
                    )}
                  </Button>
                );
              })}
            </Box>
          </SurfaceCard>
        </Grid>

        <Grid item xs={12} lg={4}>
          <SurfaceCard sx={{ p: { xs: 2.5, md: 3 }, height: "100%" }}>
            <Typography variant="h4" sx={{ mb: 0.8 }}>
              {selectedDate.toLocaleDateString(undefined, {
                weekday: "long",
                day: "2-digit",
                month: "long",
                year: "numeric",
              })}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2.4 }}>
              Events scheduled for the selected date.
            </Typography>

            {selectedEvents.length === 0 ? (
              <Typography color="text.secondary">No events scheduled here.</Typography>
            ) : (
              <Stack spacing={1.4}>
                {selectedEvents.map((event) => (
                  <SurfaceCard key={event.id} sx={{ p: 2, bgcolor: "rgba(255,255,255,0.82)" }}>
                    <Stack spacing={0.8}>
                      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                        <Chip label={event.category} size="small" />
                        {event.priority && <Chip label="Priority" size="small" color="warning" />}
                        {event.type === "recurring" && <Chip label="Yearly" size="small" sx={{ bgcolor: "rgba(124, 58, 237, 0.12)", color: "#7c3aed" }} />}
                      </Stack>
                      <Typography variant="h5">{event.title}</Typography>
                      {event.notes && (
                        <Typography variant="body2" color="text.secondary">
                          {event.notes}
                        </Typography>
                      )}
                    </Stack>
                  </SurfaceCard>
                ))}
              </Stack>
            )}
          </SurfaceCard>
        </Grid>
      </Grid>
    </PageShell>
  );
};

export default CalendarView;
